import React, { useMemo, useState } from 'react';
import {
    buildAccuracyEvidencePanelModel,
    isAccuracyEvidencePanelAvailable,
} from '../../../utils/accuracyEvidenceMeta';
import EvidenceSourceBadge from './EvidenceSourceBadge';
import EvidenceDecisionBadge from './EvidenceDecisionBadge';
import styles from '../css/index.module.css';

function EvidenceItemRow({ item }) {
    const headline = item.filename || item.title;

    return (
        <li className={styles.aiWardenEvidenceItem}>
            <div className={styles.aiWardenEvidenceItemHeader}>
                {item.ref ? (
                    <span className={styles.aiWardenEvidenceItemRef}>{item.ref}</span>
                ) : null}
                <span className={styles.aiWardenEvidenceItemTitle}>{headline}</span>
            </div>
            <div className={styles.aiWardenEvidenceItemMeta}>
                {item.bucket ? <EvidenceSourceBadge bucket={item.bucket} label={item.sourceLabel} /> : null}
                {item.decision ? (
                    <EvidenceDecisionBadge decision={item.decision} label={item.decisionLabel} />
                ) : null}
                {item.score != null ? (
                    <span className={styles.aiWardenEvidenceItemScore}>Score {item.score}</span>
                ) : null}
            </div>
            {item.reasonRetained ? (
                <p className={styles.aiWardenEvidenceItemDetail}>Reason: {item.reasonRetained}</p>
            ) : null}
            {item.authorityClass ? (
                <p className={styles.aiWardenEvidenceItemDetail}>Authority: {item.authorityClass}</p>
            ) : null}
            {item.authorityDetail ? (
                <p className={styles.aiWardenEvidenceItemDetail}>Rerank: {item.authorityDetail}</p>
            ) : null}
            {item.citationLabel ? (
                <p className={styles.aiWardenEvidenceItemDetail}>Citation: {item.citationLabel}</p>
            ) : null}
            {item.detail ? <p className={styles.aiWardenEvidenceItemDetail}>{item.detail}</p> : null}
        </li>
    );
}

function ExcludedRow({ item }) {
    return (
        <li className={styles.aiWardenEvidenceItem}>
            <span className={styles.aiWardenEvidenceItemTitle}>{item.title}</span>
            <span className={styles.aiWardenEvidenceExcludedCount}>×{item.count}</span>
        </li>
    );
}

const SECTIONS_WITH_EMPTY = new Set(['proof', 'management', 'consultant', 'contradictions']);

const AccuracyEvidencePanel = ({
    accuracyMode,
    citations = [],
    evidence = [],
    retrievalDebug,
    chunks = [],
    displayCtx = {},
}) => {
    const [open, setOpen] = useState(false);

    const available = isAccuracyEvidencePanelAvailable(
        accuracyMode,
        retrievalDebug,
        citations,
        evidence,
    );

    const model = useMemo(
        () =>
            buildAccuracyEvidencePanelModel({
                citations,
                evidence,
                retrievalDebug,
                chunks,
                ctx: displayCtx,
            }),
        [citations, evidence, retrievalDebug, chunks, displayCtx],
    );

    if (!available) return null;

    return (
        <div className={styles.aiWardenAccuracyEvidence}>
            <button
                type="button"
                className={styles.aiWardenAccuracyEvidenceToggle}
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}>
                {open ? 'Hide' : 'Show'} evidence used
            </button>
            {open ? (
                <div className={styles.aiWardenAccuracyEvidenceBody}>
                    <div className={styles.aiWardenAccuracyEvidenceHeader}>
                        <h4 className={styles.aiWardenAccuracyEvidenceTitle}>{model.title}</h4>
                        <p className={styles.aiWardenAccuracyEvidenceSubtitle}>{model.subtitle}</p>
                    </div>
                    {!model.hasSectionContent && !model.filterActive ? (
                        <p className={styles.aiWardenAccuracyEvidenceEmpty}>{model.emptyMessage}</p>
                    ) : (
                        model.sections.map(section => {
                            const isExcluded = section.key === 'excluded';
                            const showEmpty =
                                SECTIONS_WITH_EMPTY.has(section.key) &&
                                !section.items?.length &&
                                section.emptyMessage;
                            if (!section.items?.length && !showEmpty) return null;

                            return (
                                <div key={section.key} className={styles.aiWardenAccuracyEvidenceSection}>
                                    <div className={styles.aiWardenAccuracyEvidenceSectionTitle}>
                                        {section.title}
                                    </div>
                                    {showEmpty ? (
                                        <p className={styles.aiWardenAccuracyEvidenceSectionEmpty}>
                                            {section.emptyMessage}
                                        </p>
                                    ) : (
                                        <ul className={styles.aiWardenAccuracyEvidenceList}>
                                            {section.items.map(item =>
                                                isExcluded ? (
                                                    <ExcludedRow key={item.key} item={item} />
                                                ) : (
                                                    <EvidenceItemRow key={item.key} item={item} />
                                                ),
                                            )}
                                        </ul>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default AccuracyEvidencePanel;
